import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom/client';
import AccessRulesModal from './AccessRulesModal';
import PageAccessService from './PageAccessService';
import apiClient from '../../assets/scripts/services/apiClient';
import authService from '../../assets/scripts/auth/authService';

const ApplicationAccessManager = () => {
  const [applications, setApplications] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [selectedOrganization, setSelectedOrganization] = useState(null);
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagesLoading, setPagesLoading] = useState(false);
  const [error, setError] = useState(null);
  const [rulesModalOpen, setRulesModalOpen] = useState(false);
  const [selectedPage, setSelectedPage] = useState(null);

  useEffect(() => {
    checkAuthAndLoadData();
  }, []);

  const checkAuthAndLoadData = async () => {
    try {
      const isAuthenticated = await authService.requireAuth();
      if (!isAuthenticated) {
        return;
      }
      await loadApplications();
    } catch (err) {
      console.error('Error in checkAuthAndLoadData:', err);
      setError('Failed to initialize application access manager');
      setLoading(false);
    }
  };

  const loadApplications = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await apiClient.get('/applications');
      
      // Filter out service worker applications
      const nonServiceWorkerApps = (data || []).filter(app => !app.is_service_worker);
      setApplications(nonServiceWorkerApps);
    } catch (err) {
      console.error('Error loading applications:', err);
      setError('Failed to load applications');
    } finally {
      setLoading(false);
    }
  };

  const handleApplicationSelect = (applicationId) => {
    const app = applications.find(a => a.id === parseInt(applicationId));
    setSelectedApplication(app);
    setSelectedOrganization(null);
    setPages([]);
    
    // Load organizations for this application
    if (app && app.organizations) {
      setOrganizations(app.organizations);
    } else {
      setOrganizations([]);
    }
  };

  const handleOrganizationSelect = async (organizationId) => {
    const org = organizations.find(o => o.id === parseInt(organizationId));
    setSelectedOrganization(org);
    
    if (selectedApplication && organizationId) {
      await loadApplicationPages();
    }
  };

  const loadApplicationPages = async () => {
    if (!selectedApplication) return;
    
    setPagesLoading(true);
    try {
      const response = await apiClient.get(`/applications/${selectedApplication.id}/pages`);
      
      // Handle different response formats
      const pagesData = Array.isArray(response) ? response : 
                       response?.items || response?.pages || [];
      
      setPages(pagesData);
    } catch (err) {
      console.error('Error loading application pages:', err);
      setPages([]);
    } finally {
      setPagesLoading(false);
    }
  };

  const handleSetAccessRules = (page) => {
    setSelectedPage(page);
    setRulesModalOpen(true);
  };

  const handleSaveAccessRules = async (pageId, accessRules) => {
    try {
      if (!selectedOrganization?.id) {
        throw new Error('Organization ID is required to update page access rules');
      }
      
      // Use the PageAccessService to update with required organization_id
      const updatedPage = await PageAccessService.updatePageRestrictions(
        pageId, 
        selectedOrganization.id,
        accessRules
      );
      
      // Update local state with organization-specific structure
      setPages(pages.map(p => {
        if (p.id === pageId) {
          // Update the page with the new organization-specific rules
          const updatedRestrictions = {
            ...p.restricted_access_options,
            [selectedOrganization.id]: accessRules
          };
          return { ...p, restricted_access_options: updatedRestrictions };
        }
        return p;
      }));
      
      showNotification('Access rules saved successfully', 'success');
    } catch (err) {
      console.error('Error saving access rules:', err);
      throw err; // Let the modal handle the error display
    }
  };

  const showNotification = (message, type = 'info') => {
    if (type === 'error') {
      alert('Error: ' + message);
    } else {
      alert(message);
    }
  };

  const hasAccessRules = (page) => {
    const accessOptions = page.restricted_access_options;
    
    // Handle new nested organization-specific format
    if (selectedOrganization?.id && accessOptions) {
      // Check for organization-specific rules first
      const orgRules = accessOptions[selectedOrganization.id];
      if (orgRules) {
        return (orgRules.user_types && orgRules.user_types.length > 0) ||
               (orgRules.user_list && orgRules.user_list.length > 0);
      }
      
      // Fall back to default rules if no org-specific rules
      const defaultRules = accessOptions.default || accessOptions;
      return (defaultRules.user_types && defaultRules.user_types.length > 0) ||
             (defaultRules.user_list && defaultRules.user_list.length > 0);
    }
    
    // Legacy format handling
    return accessOptions && (
      (accessOptions.user_types && accessOptions.user_types.length > 0) ||
      (accessOptions.user_list && accessOptions.user_list.length > 0)
    );
  };

  const getAccessSummary = (page) => {
    const accessOptions = page.restricted_access_options;
    if (!accessOptions) return 'No restrictions';
    
    let userTypes = [];
    let userList = [];
    
    // Handle new nested organization-specific format
    if (selectedOrganization?.id && typeof accessOptions === 'object') {
      // Check for organization-specific rules first
      const orgRules = accessOptions[selectedOrganization.id];
      if (orgRules) {
        userTypes = orgRules.user_types || [];
        userList = orgRules.user_list || [];
      } else {
        // Fall back to default rules if no org-specific rules
        const defaultRules = accessOptions.default || accessOptions;
        userTypes = defaultRules.user_types || [];
        userList = defaultRules.user_list || [];
      }
    } else {
      // Legacy format
      userTypes = accessOptions.user_types || [];
      userList = accessOptions.user_list || [];
    }
    
    if (userTypes.length === 0 && userList.length === 0) {
      return 'No restrictions';
    }
    
    const summary = [];
    if (userTypes.length > 0) {
      summary.push(`${userTypes.length} user type${userTypes.length !== 1 ? 's' : ''}`);
    }
    if (userList.length > 0) {
      summary.push(`${userList.length} individual user${userList.length !== 1 ? 's' : ''}`);
    }
    
    return summary.join(' + ');
  };

  if (loading) {
    return (
      <div className="text-center p-4">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-danger" role="alert">
        {error}
      </div>
    );
  }

  return (
    <div className="application-access-manager">
      <div className="container-fluid">
        <div className="row">
          <div className="col-12">
            <div className="peers ai-c jc-sb fxw-nw mB-30">
              <div className="peer">
                <h4 className="c-grey-900 mT-10 mB-0">Application Access Manager</h4>
                <p className="c-grey-600 mB-0">Manage page-level access controls for your applications</p>
              </div>
            </div>
          </div>
        </div>

        {/* Application and Organization Selection */}
        <div className="row">
          <div className="col-12">
            <div className="bgc-white bd bdrs-3 p-20 mB-20">
              <h6 className="c-grey-900 mB-20">Select Application and Organization</h6>
              
              <div className="row">
                <div className="col-md-6">
                  <label className="form-label">
                    Select Application <span className="text-danger">*</span>
                  </label>
                  <select 
                    className="form-control"
                    value={selectedApplication?.id || ''}
                    onChange={(e) => handleApplicationSelect(e.target.value)}
                  >
                    <option value="">Choose an application...</option>
                    {applications.map(app => (
                      <option key={app.id} value={app.id}>
                        {app.name}
                        {app.description && ` - ${app.description}`}
                      </option>
                    ))}
                  </select>
                  {applications.length === 0 && (
                    <div className="form-text text-muted">
                      No regular applications found. Service worker applications don't support page access controls.
                    </div>
                  )}
                </div>
                
                <div className="col-md-6">
                  <label className="form-label">
                    Select Organization <span className="text-danger">*</span>
                  </label>
                  <select 
                    className="form-control"
                    value={selectedOrganization?.id || ''}
                    onChange={(e) => handleOrganizationSelect(e.target.value)}
                    disabled={!selectedApplication}
                  >
                    <option value="">Choose an organization...</option>
                    {organizations.map(org => (
                      <option key={org.id} value={org.id}>
                        {org.name || `Organization ${org.id}`}
                      </option>
                    ))}
                  </select>
                  {selectedApplication && organizations.length === 0 && (
                    <div className="form-text text-warning">
                      This application has no associated organizations.
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Pages List */}
        {selectedApplication && selectedOrganization && (
          <div className="row">
            <div className="col-12">
              <div className="bgc-white bd bdrs-3 p-20 mB-20">
                <div className="d-flex justify-content-between align-items-center mB-20">
                  <div>
                    <h5 className="c-grey-900 mB-5">
                      Application Pages - {selectedApplication.name}
                    </h5>
                    <p className="c-grey-600 mB-0">
                      Organization: {selectedOrganization.name || `Organization ${selectedOrganization.id}`}
                    </p>
                  </div>
                  <button 
                    className="btn btn-outline-secondary btn-sm"
                    onClick={loadApplicationPages}
                    disabled={pagesLoading}
                  >
                    <i 
                      className="ti-reload" 
                      style={{
                        animation: pagesLoading ? 'spin 1s linear infinite' : 'none'
                      }}
                    ></i>
                    Refresh
                  </button>
                </div>
                
                {pagesLoading ? (
                  <div className="text-center p-4">
                    <div className="spinner-border" role="status">
                      <span className="visually-hidden">Loading pages...</span>
                    </div>
                  </div>
                ) : pages.length === 0 ? (
                  <div className="text-center text-muted py-5">
                    <i className="ti-files display-4 mb-3 d-block"></i>
                    <h6>No Pages Found</h6>
                    <p>This application doesn't have any registered pages yet.</p>
                    <p className="small">
                      Pages can be added through the 
                      <a href="applications.html" className="ms-1">Application Management</a> page.
                    </p>
                  </div>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-striped table-hover">
                      <thead>
                        <tr>
                          <th style={{ width: '50px' }}>#</th>
                          <th>Page Title</th>
                          <th>URL</th>
                          <th>Current Access Rules</th>
                          <th style={{ width: '150px' }}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pages.map((page, index) => (
                          <tr key={page.id}>
                            <td className="text-muted">{index + 1}</td>
                            <td>
                              <strong>{page.page_title}</strong>
                            </td>
                            <td>
                              <a 
                                href={page.url} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="text-primary text-decoration-none"
                              >
                                <i className="ti-link me-1"></i>
                                {page.url}
                              </a>
                            </td>
                            <td>
                              <div className="d-flex align-items-center">
                                {hasAccessRules(page) ? (
                                  <>
                                    <span className="badge bg-success me-2">
                                      <i className="ti-shield me-1"></i>
                                      Restricted
                                    </span>
                                    <small className="text-muted">
                                      {getAccessSummary(page)}
                                    </small>
                                  </>
                                ) : (
                                  <span className="badge bg-secondary">
                                    <i className="ti-world me-1"></i>
                                    Open Access
                                  </span>
                                )}
                              </div>
                            </td>
                            <td>
                              <button
                                className="btn btn-sm btn-primary"
                                onClick={() => handleSetAccessRules(page)}
                                title="Set Access Rules"
                              >
                                <i className="ti-shield me-1"></i>
                                Set Access Rules
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Getting Started Guide */}
        {!selectedApplication && (
          <div className="row">
            <div className="col-12">
              <div className="bgc-white bd bdrs-3 p-20">
                <div className="text-center">
                  <i className="ti-shield display-3 c-blue-500 mb-3 d-block"></i>
                  <h5 className="c-grey-900 mb-3">Application Access Manager</h5>
                  <p className="c-grey-600 mb-4">
                    Control who can access specific pages within your applications by setting up user-based and role-based access rules.
                  </p>
                  
                  <div className="row justify-content-center">
                    <div className="col-md-8">
                      <div className="card">
                        <div className="card-body">
                          <h6 className="card-title">Getting Started</h6>
                          <ol className="text-start">
                            <li className="mb-2">
                              <strong>Select an Application:</strong> Choose from your non-service worker applications
                            </li>
                            <li className="mb-2">
                              <strong>Choose Organization:</strong> Select which organization's access to manage
                            </li>
                            <li className="mb-2">
                              <strong>Set Page Rules:</strong> Configure access rules for each registered page
                            </li>
                            <li>
                              <strong>Define Access:</strong> Grant access by user type or individual users
                            </li>
                          </ol>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Access Rules Modal */}
      <AccessRulesModal
        isOpen={rulesModalOpen}
        onClose={() => setRulesModalOpen(false)}
        onSave={handleSaveAccessRules}
        page={selectedPage}
        applicationId={selectedApplication?.id}
        organizationId={selectedOrganization?.id}
      />

    </div>
  );
};

export default ApplicationAccessManager;

export const initApplicationAccessManager = () => {
  const mountElement = document.getElementById('application-access-root');
  if (mountElement) {
    try {
      const root = ReactDOM.createRoot(mountElement);
      root.render(<ApplicationAccessManager />);
    } catch (err) {
      console.error('Error rendering ApplicationAccessManager:', err);
    }
  } else {
    console.error('Mount element "application-access-root" not found');
  }
};